import { apiService } from './api';
import type { LoanSummaryReport, LessonPlanSummaryReport } from '../types/reports';

export interface MonthlyLoanData {
  month: string;
  loans: number;
  returns: number;
  lessonPlansSubmitted: number;
  lessonPlansCancelled: number;
}

export interface ResourceTypeData {
  type: string;
  count: number;
}

export interface AllBooksData {
  type: string;
  count: number;
}

export interface TopResourceData {
  id: string;
  title: string;
  count: number;
}

export interface TopUserData {
  id: string;
  fullName: string;
  cmsUserId: string;
  count: number;
}

export interface BooksByCategoryData {
  category: string;
  bookCount: number; // Số loại sách (unique books)
  totalCopies: number; // Tổng số bản sao
  availableCopies: number; // Số bản sao có sẵn
  loanedCopies: number; // Số bản sao đang mượn
}

export interface OverviewStats {
  totalBooks: number;           // Backward compatibility (= totalBookCopies)
  totalBookCopies?: number;     // Tổng số bản sao sách
  totalBookTitles?: number;     // Số đầu sách (unique titles)
  totalResources: number;
  totalLessonPlans: number;
  totalUsers: number;
}

export interface AdminStats {
  booksByCategory: Array<{ category: string; count: number }>;
  loanStats: {
    totalCopies: number;
    availableCopies: number;
    borrowedCopies: number;
    loanRate: string;
    returnRate: string;
  };
  overdueBooks: Array<{
    id: string;
    loanId: string;
    bookId: string;
    userId: string;
    title: string;
    borrower: string;
    dueDate: string;
    daysOverdue: number;
  }>;
  popularResources: Array<{
    id: string;
    title: string;
    type: string;
    views: number;
    downloads: number;
  }>;
}

export interface LessonPlanRatio {
  totalSubmitted: number;
  approved: number;
  pending: number;
  needsRevision: number;
  approvalRate: string;
  pendingRate: string;
  revisionRate: string;
}

export interface LoanReturnStats {
  onTime: number;
  overdue: number;
  currentlyOverdue: number;
  total: number;
  onTimeRate: string;
  overdueRate: string;
}

export interface ResourceViewsDownloadsTimeline {
  month: string;
  views: number;
  downloads: number;
  loans: number;
}

export const reportsService = {
  async getLoanSummary(): Promise<LoanSummaryReport> {
    const response = await apiService.get('/api/reports/loan-summary');
    return response as LoanSummaryReport;
  },

  async getLessonPlanSummary(): Promise<LessonPlanSummaryReport> {
    const response = await apiService.get('/api/reports/lesson-plan-summary');
    return response as LessonPlanSummaryReport;
  },

  async getMonthlyLoans(): Promise<MonthlyLoanData[]> {
    const response = await apiService.get('/api/reports/monthly-loans');
    return response as MonthlyLoanData[];
  },

  async getResourceTypes(): Promise<ResourceTypeData[]> {
    const response = await apiService.get('/api/reports/resource-types');
    return response as ResourceTypeData[];
  },

  async getAllBooks(): Promise<AllBooksData[]> {
    const response = await apiService.get('/api/reports/all-books');
    return response as AllBooksData[];
  },

  async getTopDownloadedResources(): Promise<TopResourceData[]> {
    const response = await apiService.get('/api/reports/top-downloaded-resources');
    return response as TopResourceData[];
  },

  async getTopViewedResources(): Promise<TopResourceData[]> {
    const response = await apiService.get('/api/reports/top-viewed-resources');
    return response as TopResourceData[];
  },

  async getTopBorrowingUsers(): Promise<TopUserData[]> {
    const response = await apiService.get('/api/reports/top-borrowing-users');
    return response as TopUserData[];
  },

  async getOverviewStats(): Promise<OverviewStats> {
    const response = await apiService.get('/api/reports/overview-stats');
    return response as OverviewStats;
  },

  async getAdminStats(params?: {
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AdminStats> {
    const searchParams = new URLSearchParams();
    if (params?.search && params.search.trim()) {
      searchParams.append('search', params.search.trim());
    }
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate);
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/reports/admin-stats?${queryString}` : '/api/reports/admin-stats';
    const response = await apiService.get(endpoint);
    return response as AdminStats;
  },

  async getLessonPlanRatio(): Promise<LessonPlanRatio> {
    const response = await apiService.get('/api/reports/lesson-plan-ratio');
    return response as LessonPlanRatio;
  },

  async getBooksByCategory(): Promise<BooksByCategoryData[]> {
    const response = await apiService.get('/api/reports/books-by-category');
    return response as BooksByCategoryData[];
  },

  async getLoanReturnStats(): Promise<LoanReturnStats> {
    const response = await apiService.get('/api/reports/loan-return-stats');
    return response as LoanReturnStats;
  },

  async getResourceViewsDownloadsTimeline(): Promise<ResourceViewsDownloadsTimeline[]> {
    const response = await apiService.get('/api/reports/resource-views-downloads-timeline');
    return response as ResourceViewsDownloadsTimeline[];
  },

  async getTopEarlyLessonPlanSubmitters(): Promise<TopUserData[]> {
    const response = await apiService.get('/api/reports/top-early-lesson-plan-submitters');
    return response as TopUserData[];
  },

  async getTopResourceContributors(): Promise<TopUserData[]> {
    const response = await apiService.get('/api/reports/top-resource-contributors');
    return response as TopUserData[];
  },

  async getTopLessonPlanSubmitters(): Promise<TopUserData[]> {
    const response = await apiService.get('/api/reports/top-lesson-plan-submitters');
    return response as TopUserData[];
  }
};
